import { useEffect, useState } from 'react';
import { getHomeContent } from '@/lib/api';

// TikTok icon (lucide-react doesn't have TikTok, use custom SVG)
function TikTokIcon({ className = 'w-6 h-6' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}

// Extract TikTok video ID from a URL like https://www.tiktok.com/@user/video/1234567890
function extractVideoId(url) {
  if (!url) return null;
  const match = url.match(/\/video\/(\d+)/);
  return match ? match[1] : null;
}

function TikTokEmbed({ url }) {
  const videoId = extractVideoId(url);
  if (!videoId) return null;
  return (
    <div className="w-full max-w-[325px] mx-auto">
      <iframe
        src={`https://www.tiktok.com/embed/v2/${videoId}`}
        width="100%"
        height="580"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        title={`TikTok video ${videoId}`}
        className="border border-zinc-800 bg-zinc-900"
        data-testid={`tiktok-embed-${videoId}`}
      />
    </div>
  );
}

export function TikTokSection() {
  const [videos, setVideos] = useState([]);
  const [profileUrl, setProfileUrl] = useState('https://www.tiktok.com/@nakituneperformance');

  useEffect(() => {
    getHomeContent()
      .then((res) => {
        const d = res.data || {};
        setVideos(Array.isArray(d.tiktok_video_urls) ? d.tiktok_video_urls.filter(Boolean) : []);
        if (d.tiktok_profile_url) setProfileUrl(d.tiktok_profile_url);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="relative z-10 py-16 border-t border-zinc-800" data-testid="tiktok-section">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <TikTokIcon className="w-6 h-6 text-green-500" />
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            TikTok
          </h2>
        </div>

        {videos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {videos.map((url) => (
              <TikTokEmbed key={url} url={url} />
            ))}
          </div>
        ) : (
          <div className="text-center">
            <p className="text-zinc-500 mb-4 text-sm">TikTok videos coming soon</p>
          </div>
        )}

        <div className="text-center mt-6">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 text-sm"
            data-testid="tiktok-profile-link"
          >
            <TikTokIcon className="w-4 h-4" />
            Follow us on TikTok
          </a>
        </div>
      </div>
    </section>
  );
}

export { TikTokIcon };
